/**
 * Typed accessors for domain entity fields (RM: semantic i18n). One pattern for
 * everything nameable — a field AND each of its enum values is `{ label, hint? }`:
 *   entity.<type>.field.<name> = { label, hint?, placeholder?, value?, error? }
 *   ...field.<name>.value.<v>  = { label, hint? }
 * so a property's label, values, and helper text read the same everywhere the
 * entity appears (form, detail, summary). Add a field = one block in the locales.
 */
import { useTranslation } from 'react-i18next'
import type {
  JobKind,
  UpscalerKind,
  InvestmentUpscaler,
} from '../pages/newJobForm'

export function useDomainLabels() {
  const { t } = useTranslation()

  const label = (entity: string, field: string) =>
    t(`entity.${entity}.field.${field}.label`)
  const hint = (entity: string, field: string) =>
    t(`entity.${entity}.field.${field}.hint`)
  const placeholder = (entity: string, field: string) =>
    t(`entity.${entity}.field.${field}.placeholder`)
  const value = (entity: string, field: string, v: string) =>
    t(`entity.${entity}.field.${field}.value.${v}.label`)
  const valueHint = (entity: string, field: string, v: string) =>
    t(`entity.${entity}.field.${field}.value.${v}.hint`)

  return {
    label,
    hint,
    placeholder,
    value,
    valueHint,
    // Typed shortcuts for the enum-valued fields used across the app.
    jobKind: (kind: JobKind) => value('job', 'kind', kind),
    jobKindHint: (kind: JobKind) => valueHint('job', 'kind', kind),
    engine: (engine: string) => value('blenderRender', 'engine', engine),
    outputFormat: (format: string) =>
      value('blenderRender', 'format', format),
    upscaler: (upscaler: UpscalerKind) =>
      value('upscaling', 'upscaler', upscaler),
    investmentOverlay: (upscaler: InvestmentUpscaler) =>
      value('upscaling', 'investmentOverlay', upscaler),
  }
}
